import { ArrowRight, Info } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { ROLES } from './roles';

const Section = ({ s, roleId }) => (
  <AccordionItem value={s.id} className="border-slate-100" data-testid={`opening-${roleId}-section-${s.id}`}>
    <AccordionTrigger className="text-sm font-semibold text-slate-900 hover:no-underline py-3.5" data-testid={`opening-${roleId}-toggle-${s.id}`}>{s.title}</AccordionTrigger>
    <AccordionContent>
      {s.paragraphs && <div className="space-y-3 text-sm text-slate-600 leading-relaxed">{s.paragraphs.map((p) => <p key={p}>{p}</p>)}</div>}
      {s.items && (
        <ul className="space-y-3">
          {s.items.map(({ icon: Icon, title, text }) => (
            <li key={title} className="flex gap-3">
              <span className="w-8 h-8 shrink-0 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center"><Icon className="w-4 h-4" /></span>
              <div><p className="text-sm font-semibold text-slate-900">{title}</p><p className="text-sm text-slate-600 leading-relaxed">{text}</p></div>
            </li>
          ))}
        </ul>
      )}
      {s.bullets && (
        <ul className={`space-y-2 ${s.id === 'conditions' ? 'rounded-xl border border-amber-200 bg-amber-50 p-4' : ''}`}>
          {s.bullets.map((b) => (
            <li key={b} className="flex gap-2 text-sm text-slate-700">
              {s.id === 'conditions' ? <Info className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" /> : <span className="mt-2 w-1.5 h-1.5 rounded-full bg-teal-500 shrink-0" />}{b}
            </li>
          ))}
        </ul>
      )}
    </AccordionContent>
  </AccordionItem>
);

const OpeningCard = ({ role, active, onApply }) => {
  const { id, icon: Icon, tag, title, headline, text, facts, sections } = role;
  return (
    <article data-testid={`opening-card-${id}`}
      className={`flex flex-col bg-white rounded-2xl p-6 md:p-7 border transition-[box-shadow,border-color] duration-300 ${active ? 'border-teal-400 shadow-lg' : 'border-slate-200 hover:border-teal-200 hover:shadow-lg'}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="w-11 h-11 rounded-xl bg-[#0b1f4d] text-white flex items-center justify-center"><Icon className="w-5 h-5" /></div>
        <span className="text-xs font-semibold text-teal-700 bg-teal-50 border border-teal-100 rounded-full px-3 py-1">{tag}</span>
      </div>
      <h3 className="mt-4 text-xl font-bold text-slate-900">{title}</h3>
      <p className="mt-1 text-sm font-semibold text-teal-700">{headline}</p>
      <p className="mt-2 text-sm text-slate-600 leading-relaxed">{text}</p>
      <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
        {facts.map(({ icon: FIcon, label, value }) => (
          <div key={label} className="rounded-xl bg-slate-50 border border-slate-100 p-3 flex gap-2.5" data-testid={`opening-${id}-fact-${label.toLowerCase().replace(' ', '-')}`}>
            <FIcon className="w-4 h-4 mt-0.5 shrink-0 text-teal-600" />
            <div><dt className="text-[11px] uppercase tracking-wide text-slate-500">{label}</dt><dd className="text-sm font-semibold text-slate-900 leading-snug">{value}</dd></div>
          </div>
        ))}
      </dl>
      <Accordion type="multiple" className="mt-4 flex-1">
        {sections.map((s) => <Section key={s.id} s={s} roleId={id} />)}
      </Accordion>
      <button type="button" onClick={() => onApply(id)} data-testid={`opening-apply-${id}`}
        className="mt-6 self-start inline-flex items-center gap-2 bg-[#0b1f4d] hover:bg-teal-600 text-white font-semibold px-7 py-3.5 rounded-full transition-colors">
        Apply Now <ArrowRight className="w-4 h-4" />
      </button>
    </article>
  );
};

export const OpeningsOverview = ({ active, onApply }) => (
  <section id="openings" className="py-14 md:py-16 bg-white scroll-mt-20" data-testid="careers-openings">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Open Opportunities</h2>
      <p className="mt-2 text-slate-600">Two ways to grow with Elevate Careers. Open a card to see the full role, then hit Apply Now.</p>
      <div className="mt-8 grid md:grid-cols-2 gap-5 items-start">
        {ROLES.map((r) => <OpeningCard key={r.id} role={r} active={active === r.id} onApply={onApply} />)}
      </div>
    </div>
  </section>
);
